// UX-035 / UX-036: one place that turns a collaborator's grant into the words
// and tone the shared screens print. Kept free of React so the mapping can be
// tested directly and the three shared screens cannot drift apart.

import type { Permission } from '../../types'

export type AccessLevel = Permission['permission_level']
export type AccessTone = 'neutral' | 'info' | 'success' | 'warning'

/** What each stored level is called on screen. `admin` reads "Manage": an admin
 * can do everything a manager can, and "Comment" for either was the bug. */
const LABELS: Record<AccessLevel, string> = {
  view: 'View',
  comment: 'Comment',
  edit: 'Edit',
  manager: 'Manage',
  admin: 'Manage',
}

const TONES: Record<AccessLevel, AccessTone> = {
  view: 'info',
  comment: 'warning',
  edit: 'success',
  manager: 'success',
  admin: 'success',
}

const WORDS: Record<AccessLevel, string> = {
  view: 'You can view this.',
  comment: 'You can view and comment.',
  edit: 'You can view, comment and edit.',
  manager: 'You can view, comment, edit and manage who has access.',
  admin: 'You can view, comment, edit and manage who has access.',
}

function isLevel(level: unknown): level is AccessLevel {
  return typeof level === 'string' && Object.prototype.hasOwnProperty.call(LABELS, level)
}

/** The user's own grant on a resource. Matches by user id, then by email, so an
 * email grant that was made before the person signed in is still found. Group
 * grants are not resolvable here — callers must treat "no match" as unknown,
 * never as "View". */
export function findMyGrant(
  permissions: Permission[] | undefined,
  user: { id?: string; email?: string } | null | undefined,
): Permission | undefined {
  if (!permissions || !user) return undefined
  const byId = user.id ? permissions.find((p) => p.grantee_id === user.id) : undefined
  if (byId) return byId
  const email = (user.email || '').trim().toLowerCase()
  if (!email) return undefined
  return permissions.find((p) => (p.grantee_email || '').trim().toLowerCase() === email)
}

export interface AccessBadge {
  /** 'loading' → show a skeleton chip; 'unknown' → grant not resolvable. */
  state: 'loading' | 'known' | 'unknown'
  label: string
  tone: AccessTone
  level?: AccessLevel
  canDownload: boolean
}

/** The single access badge for a resource. Never defaults to "View": while the
 * grant is loading it says so, and when no grant can be matched it says only
 * that the project is shared. */
export function accessBadge(
  grant: Permission | undefined,
  { loading = false }: { loading?: boolean } = {},
): AccessBadge {
  if (loading) return { state: 'loading', label: '', tone: 'neutral', canDownload: false }
  if (!grant || !isLevel(grant.permission_level)) {
    return { state: 'unknown', label: 'Shared with you', tone: 'neutral', canDownload: false }
  }
  return {
    state: 'known',
    label: LABELS[grant.permission_level],
    tone: TONES[grant.permission_level],
    level: grant.permission_level,
    canDownload: !!grant.allow_download,
  }
}

/** Plain-words statement of what the person can do. */
export function accessInWords(grant: Permission | undefined): string {
  if (!grant || !isLevel(grant.permission_level)) return 'This was shared with you.'
  return WORDS[grant.permission_level]
}

/** Visible explanation for a disabled action — shown as text, not a tooltip,
 * so it reaches touch and keyboard users. */
export function lockedActionReason(action: 'Download' | 'Print'): string {
  return `${action} is locked at your access level. Ask the person who shared this to raise it.`
}
